import {
	PEG_COUNT,
	PEG_TRACK,
	type PegChaseState,
	type PlayerIndex,
	squareOf,
} from "@playpal/core";
import { Die } from "../components/Die";

interface Props {
	state: PegChaseState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

export function PegChaseBoard({ state, seat, onMove }: Props) {
	const myTurn = state.turn === seat && !state.result;
	const rival: PlayerIndex = seat === 0 ? 1 : 0;
	const movable = new Set(
		myTurn && state.roll !== null
			? state.pegs[seat]
					.map((_, index) => index)
					.filter((index) => {
						const peg = state.pegs[seat][index];
						if (peg < 0) return state.roll === 6;
						return peg + (state.roll ?? 0) <= PEG_TRACK;
					})
			: [],
	);

	/** Who is standing on each square of the shared loop. */
	const occupant = (square: number): PlayerIndex | null => {
		if (state.pegs[seat].some((peg) => squareOf(seat, peg) === square)) return seat;
		if (state.pegs[rival].some((peg) => squareOf(rival, peg) === square)) return rival;
		return null;
	};

	return (
		<div className="board-frame board-md">
			<p className="mb-3 text-center text-lg font-semibold">
				Home — you {state.scores[seat]} of {PEG_COUNT}, them {state.scores[rival]}
			</p>

			{/* The loop, laid out as a ring of squares. */}
			<div className="mb-4 grid grid-cols-6 gap-1">
				{Array.from({ length: PEG_TRACK }, (_, square) => {
					const who = occupant(square);
					return (
						<div
							key={square}
							className={`flex aspect-square items-center justify-center rounded-lg text-xl ${
								who === null
									? "bg-white/60 dark:bg-white/10"
									: who === seat
										? "bg-rose-400"
										: "bg-sky-400"
							}`}
						>
							{who === null ? "" : who === seat ? "🔴" : "🔵"}
						</div>
					);
				})}
			</div>

			<p className="mb-3 text-center text-sm opacity-70">{state.lastNote}</p>

			{state.roll === null ? (
				<button
					type="button"
					disabled={!myTurn}
					onClick={() => onMove({ roll: true })}
					className="w-full rounded-2xl bg-indigo-600 px-4 py-4 text-2xl font-black text-white shadow disabled:opacity-40"
				>
					🎲 Roll
				</button>
			) : (
				<div>
					{/* The cube is decoration; the number beside it is the fact. */}
					<p className="mb-2 flex items-center justify-center gap-3">
						<Die value={state.roll} roll={state.rollsUsed} />
						<span className="text-4xl font-black">{state.roll}</span>
					</p>
					<p className="mb-2 text-center font-semibold">
						{myTurn ? "Pick a peg to move" : "Waiting for your friend…"}
					</p>
					<div className="flex justify-center gap-3">
						{state.pegs[seat].map((peg, index) => (
							<button
								key={`${peg}-${index}`}
								type="button"
								disabled={!movable.has(index)}
								onClick={() => onMove({ peg: index })}
								aria-label={
									peg < 0 ? "Peg waiting to start" : peg >= PEG_TRACK ? "Peg home" : `Peg on ${peg}`
								}
								className="flex h-20 w-20 flex-col items-center justify-center rounded-2xl bg-white text-indigo-950 shadow disabled:opacity-40 dark:bg-white/15 dark:text-white"
							>
								<span className="text-2xl">{peg >= PEG_TRACK ? "🏠" : "🔴"}</span>
								<span className="text-xs font-semibold">
									{peg < 0 ? "waiting" : peg >= PEG_TRACK ? "home" : `${PEG_TRACK - peg} to go`}
								</span>
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
